import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 * When autoplay is off, an optional play button (ref="playButton") starts playback and hides on play.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [playButton] - Optional play button when autoplay is disabled.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement, playButton } = this.refs;
    const signal = this.#abortController.signal;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    if (playButton) {
      playButton.addEventListener(
        'click',
        () => {
          videoElement.play();
        },
        { signal }
      );

      videoElement.addEventListener(
        'play',
        () => {
          this.classList.add('is-playing');
        },
        { signal }
      );

      videoElement.addEventListener(
        'pause',
        () => {
          this.classList.remove('is-playing');
        },
        { signal }
      );

      videoElement.addEventListener(
        'ended',
        () => {
          this.classList.remove('is-playing');
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
